import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, Plus, CheckCircle2 } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import ConfirmationGate, { useConfirmationGate } from "@/components/ConfirmationGate";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const STATUS_STYLE = {
  issued:  { bg: "var(--blue-light)",    color: "var(--blue-dark)", label: "Issued" },
  paid:    { bg: "var(--success-light)", color: "var(--success)",   label: "Paid" },
  overdue: { bg: "var(--alert-light)",   color: "var(--alert)",     label: "Overdue" },
};

const $ = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Levies() {
  const { user } = useAuth();
  const schemeId = getActiveSchemeId();
  const [levies, setLevies] = useState([]);
  const [statusFilter, setStatusFilter] = useState(null);
  const [me, setMe] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [oneOpen, setOneOpen] = useState(false);
  const [lots, setLots] = useState([]);

  const reload = async () => {
    const [l, lt, members] = await Promise.all([
      api.get(`/schemes/${schemeId}/levies`),
      api.get(`/schemes/${schemeId}/lots`),
      api.get(`/schemes/${schemeId}/members`),
    ]);
    setLevies(l.data);
    setLots(lt.data);
    const own = members.data.find((m) => m.user_id === user?.user_id);
    setMe(own || (user?.system_role === "platform_admin" ? { role: "chair" } : null));
  };

  useEffect(() => { reload().catch((e) => toast.error(apiError(e))); /* eslint-disable-next-line */ }, []);

  // Sprint 2H Phase A — 3 confirmation gates for LEVY_ISSUE, LEVY_ISSUE_BULK,
  // and LEVY_MARK_PAID. Each gate's onConfirmed reloads and closes any parent
  // dialog. See l1_foundation.require_confirmation.
  const issueOneGate = useConfirmationGate({
    onConfirmed: () => { setOneOpen(false); reload(); },
  });
  const bulkGate = useConfirmationGate({
    onConfirmed: () => { setBulkOpen(false); reload(); },
  });
  const markPaidGate = useConfirmationGate({ onConfirmed: reload });

  const isChairOrCommittee = me?.role === "chair" || me?.role === "committee";
  const isChair = me?.role === "chair";

  const filtered = statusFilter ? levies.filter((l) => l.status === statusFilter) : levies;
  const totals = {
    outstanding: levies.filter((l) => l.status !== "paid").reduce((s, l) => s + l.total_amount, 0),
    paid: levies.filter((l) => l.status === "paid").reduce((s, l) => s + l.total_amount, 0),
    overdue: levies.filter((l) => l.status === "overdue").length,
  };

  const markPaid = async (id) => {
    try {
      const r = await markPaidGate.fire(
        "post", `/schemes/${schemeId}/levies/${id}/mark-paid`, {},
        { __scheme_id: schemeId },
      );
      if (r) { toast.success("Marked paid"); reload(); }
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10" data-testid="levies-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--blue-dark)" }}>
              <Receipt size={12}/> Levies
            </span>
            <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }}>
              {isChairOrCommittee ? "Levy register" : "Your levies"}
            </h1>
            <p className="text-sm" style={{ color: "var(--slate)" }}>
              {isChairOrCommittee
                ? "Issue, track and mark notices paid. Bulk-issue across the scheme by period."
                : "Levy notices issued to your lots."}
            </p>
          </div>
          {isChairOrCommittee && (
            <div className="flex items-center gap-2">
              <Dialog open={oneOpen} onOpenChange={setOneOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="levy-issue-one-btn"><Plus size={12} className="mr-1.5"/> Issue one</Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <OneLevyForm lots={lots} onSubmit={async (form) => {
                    try {
                      const r = await issueOneGate.fire(
                        "post", `/schemes/${schemeId}/levies`, form,
                        { __scheme_id: schemeId },
                      );
                      if (r) { toast.success("Levy issued"); setOneOpen(false); reload(); }
                    } catch (e) { toast.error(apiError(e)); }
                  }}/>
                </DialogContent>
              </Dialog>
              <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" style={{ background: "var(--blue)", color: "white" }} data-testid="levy-bulk-btn"><Plus size={12} className="mr-1.5"/> Bulk issue</Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <BulkLevyForm onSubmit={async (form) => {
                    try {
                      const r = await bulkGate.fire(
                        "post", `/schemes/${schemeId}/levies/bulk`, form,
                        { __scheme_id: schemeId },
                      );
                      if (r) { toast.success(`Issued ${r.count} notices`); setBulkOpen(false); reload(); }
                    } catch (e) { toast.error(apiError(e)); }
                  }}/>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <Tile label="Outstanding" value={$(totals.outstanding)} color="var(--blue-dark)" testid="levy-tile-outstanding"/>
          <Tile label="Paid (YTD)"  value={$(totals.paid)} color="var(--success)" testid="levy-tile-paid"/>
          <Tile label="Overdue"     value={totals.overdue} color="var(--alert)" testid="levy-tile-overdue-count"/>
        </div>

        <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="levy-status-filter">
          {[
            { k: null, label: "All" },
            { k: "issued", label: "Open" },
            { k: "overdue", label: "Overdue" },
            { k: "paid", label: "Paid" },
          ].map((f) => (
            <button key={f.k || "all"} onClick={() => setStatusFilter(f.k)}
              data-testid={`levy-filter-${f.k || "all"}`}
              className="px-3 py-1.5 text-xs font-medium rounded-full border"
              style={{
                borderColor: statusFilter === f.k ? "var(--blue)" : "var(--border)",
                background: statusFilter === f.k ? "var(--blue-light)" : "white",
                color: statusFilter === f.k ? "var(--blue-dark)" : "var(--slate)",
              }}>{f.label}</button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="rounded-xl border bg-white px-6 py-12 text-center" style={{ borderColor: "var(--border)" }} data-testid="levy-empty">
            <Receipt size={28} className="mx-auto mb-3" style={{ color: "var(--tagline-grey)" }}/>
            <p className="text-sm" style={{ color: "var(--slate)" }}>No levies match.</p>
          </div>
        ) : (
          <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid="levy-list">
            {filtered.map((l) => {
              const st = STATUS_STYLE[l.status];
              return (
                <div key={l.levy_id} className="flex items-center gap-3 px-4 lg:px-5 py-3 border-t first:border-t-0" style={{ borderColor: "var(--border)" }} data-testid={`levy-row-${l.levy_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm" style={{ color: "var(--navy)" }}>Lot {l.lot_number}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "var(--slate-light)", color: "var(--slate)" }}>{l.period_label}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: st.bg, color: st.color }} data-testid={`levy-status-${l.levy_id}`}>{st.label}</span>
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: "var(--slate)" }}>
                      Due {new Date(l.due_date).toLocaleDateString()} · Admin {$(l.admin_fund_amount)} + Capital {$(l.capital_works_amount)}{l.special_levy_amount ? ` + Special ${$(l.special_levy_amount)}` : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold tabular-nums text-base" style={{ color: "var(--navy)" }} data-testid={`levy-total-${l.levy_id}`}>{$(l.total_amount)}</div>
                    {l.payment_recorded_at && <div className="text-[10px]" style={{ color: "var(--tagline-grey)" }}>Paid {new Date(l.payment_recorded_at).toLocaleDateString()}</div>}
                  </div>
                  {isChair && l.status !== "paid" && (
                    <Button size="sm" variant="outline" onClick={() => markPaid(l.levy_id)} data-testid={`levy-markpaid-${l.levy_id}`}>
                      <CheckCircle2 size={12} className="mr-1.5"/> Mark paid
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {/* Sprint 2H Phase A — confirmation gates for the 3 levy actions. */}
      <ConfirmationGate {...issueOneGate.gateProps} />
      <ConfirmationGate {...bulkGate.gateProps} />
      <ConfirmationGate {...markPaidGate.gateProps} />
    </AppShell>
  );
}

function Tile({ label, value, color, testid }) {
  return (
    <div className="rounded-xl border bg-white px-4 py-3" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="text-xl font-bold tabular-nums" style={{ color }}>{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-wider mt-0.5" style={{ color: "var(--tagline-grey)" }}>{label}</div>
    </div>
  );
}

function OneLevyForm({ lots, onSubmit }) {
  const [form, setForm] = useState({
    lot_id: lots[0]?.lot_id || "",
    period_label: "",
    period_start: "",
    period_end: "",
    admin_fund_amount: 1310,
    capital_works_amount: 560,
    special_levy_amount: 0,
    due_date: "",
    notes: "",
  });
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  return (
    <>
      <DialogHeader><DialogTitle>Issue levy notice</DialogTitle></DialogHeader>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit({
        ...form,
        admin_fund_amount: Number(form.admin_fund_amount),
        capital_works_amount: Number(form.capital_works_amount),
        special_levy_amount: Number(form.special_levy_amount),
      }); }} className="space-y-3 mt-2" data-testid="levy-one-form">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Lot</Label>
          <Select value={form.lot_id} onValueChange={set("lot_id")}>
            <SelectTrigger className="mt-1" data-testid="levy-lot-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              {lots.map((l) => <SelectItem key={l.lot_id} value={l.lot_id}>Lot {l.lot_number}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <FormField label="Period label" value={form.period_label} onChange={set("period_label")} placeholder="2026 Q2" testid="levy-period-input" required/>
          <FormField label="Due date" type="date" value={form.due_date} onChange={set("due_date")} testid="levy-due-input" required/>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <FormField label="Period start" type="date" value={form.period_start} onChange={set("period_start")} testid="levy-pstart-input" required/>
          <FormField label="Period end" type="date" value={form.period_end} onChange={set("period_end")} testid="levy-pend-input" required/>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <FormField label="Admin $" type="number" step="0.01" value={form.admin_fund_amount} onChange={set("admin_fund_amount")} testid="levy-admin-input"/>
          <FormField label="Capital $" type="number" step="0.01" value={form.capital_works_amount} onChange={set("capital_works_amount")} testid="levy-capital-input"/>
          <FormField label="Special $" type="number" step="0.01" value={form.special_levy_amount} onChange={set("special_levy_amount")} testid="levy-special-input"/>
        </div>
        <DialogFooter><Button type="submit" style={{ background: "var(--blue)", color: "white" }} data-testid="levy-one-submit">Issue</Button></DialogFooter>
      </form>
    </>
  );
}

function BulkLevyForm({ onSubmit }) {
  const [form, setForm] = useState({
    period_label: "",
    period_start: "",
    period_end: "",
    admin_fund_amount: 1310,
    capital_works_amount: 560,
    special_levy_amount: 0,
    due_date: "",
    notes: "",
    pro_rate_by_entitlement: true,
  });
  const set = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  return (
    <>
      <DialogHeader><DialogTitle>Bulk issue across all lots</DialogTitle></DialogHeader>
      <form onSubmit={(e) => { e.preventDefault(); onSubmit({
        ...form,
        admin_fund_amount: Number(form.admin_fund_amount),
        capital_works_amount: Number(form.capital_works_amount),
        special_levy_amount: Number(form.special_levy_amount),
      }); }} className="space-y-3 mt-2" data-testid="levy-bulk-form">
        <FormField label="Period label" value={form.period_label} onChange={set("period_label")} placeholder="2026 Q2" testid="bulk-period-input" required/>
        <div className="grid grid-cols-2 gap-2">
          <FormField label="Period start" type="date" value={form.period_start} onChange={set("period_start")} testid="bulk-pstart-input" required/>
          <FormField label="Period end" type="date" value={form.period_end} onChange={set("period_end")} testid="bulk-pend-input" required/>
        </div>
        <FormField label="Due date" type="date" value={form.due_date} onChange={set("due_date")} testid="bulk-due-input" required/>
        <div className="grid grid-cols-3 gap-2">
          <FormField label={form.pro_rate_by_entitlement ? "Admin pool $" : "Admin per-lot $"} type="number" step="0.01" value={form.admin_fund_amount} onChange={set("admin_fund_amount")} testid="bulk-admin-input"/>
          <FormField label={form.pro_rate_by_entitlement ? "Capital pool $" : "Capital per-lot $"} type="number" step="0.01" value={form.capital_works_amount} onChange={set("capital_works_amount")} testid="bulk-capital-input"/>
          <FormField label={form.pro_rate_by_entitlement ? "Special pool $" : "Special per-lot $"} type="number" step="0.01" value={form.special_levy_amount} onChange={set("special_levy_amount")} testid="bulk-special-input"/>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium" style={{ color: "var(--slate)" }}>
          <input type="checkbox" checked={form.pro_rate_by_entitlement} onChange={(e) => setForm({ ...form, pro_rate_by_entitlement: e.target.checked })} data-testid="bulk-prorate-checkbox"/>
          Pro-rate by unit entitlement (uncheck for flat per-lot)
        </label>
        <DialogFooter><Button type="submit" style={{ background: "var(--blue)", color: "white" }} data-testid="bulk-submit">Issue across all lots</Button></DialogFooter>
      </form>
    </>
  );
}

function FormField({ label, testid, ...rest }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>{label}</Label>
      <Input className="mt-1" data-testid={testid} {...rest}/>
    </div>
  );
}
